import React from "react"
import { makeStyles } from "@material-ui/core"
import ChartCard from "../../molecules/charts/ChartCard"
import BarChart from "../../molecules/charts/BarChart"
import ChartAlert from "../../molecules/charts/ChartAlert"
import EnjoymentChartLabel from "../../molecules/charts/EnjoymentChartLabel"
import { colors } from "../../../utils/theme"
import { getWeekdayAbbrev } from "../../../utils/dateTimeUtils"
import { ClientFragment } from "../../../generated/graphql"
import {
  getMoodChartIndexDate,
  getReportedMoodDays,
  getMoodTrend,
} from "../../../utils/moodChart"

type EnjoymentChartDatum = {
  dateKey: string
  enjoyment: number | null
}

/**
 * Displays the client's enjoyment ratings for the 7 reported days ending
 * yesterday.
 *
 * Mood measurements in this chart intentionally follow the existing mood-balance
 * convention from `getReportedMoodDays`: a measurement created today is treated
 * as the client's report for yesterday, so the chart excludes the current
 * calendar day and instead buckets that response under yesterday.
 *
 * Stored enjoyment values are zero-indexed in the database, but the chart renders
 * a 1-5 scale for people reading it. `getReportedMoodDays` already applies that
 * shift, so a stored value of `0` is displayed as `1` on the graph rather than as
 * an empty or invalid-looking lowest bucket.
 *
 * @param {ClientFragment["mood_measurements"]} data Client mood measurements used
 * to build the previous-week enjoyment bars and week-over-week trend alert.
 */
export default function EnjoymentChart({
  data,
  weekOffset = 0,
}: {
  data: ClientFragment["mood_measurements"]
  weekOffset?: number
}) {
  const styles = useStyles()
  const chartIndexDate = getMoodChartIndexDate(weekOffset)
  const trailingMoodDays = getReportedMoodDays(chartIndexDate, 14, data)

  const chartData: EnjoymentChartDatum[] = trailingMoodDays
    .slice(7)
    .map((datum) => ({
      dateKey: datum.date.format("YYYY-MM-DD"),
      enjoyment: datum.enjoyment ?? null,
    }))

  const thisWeekEnjoyment = trailingMoodDays
    .slice(7)
    .map((datum) => datum.enjoyment)
    .filter((value): value is number => typeof value === "number")

  const previousWeekEnjoyment = trailingMoodDays
    .slice(0, 7)
    .map((datum) => datum.enjoyment)
    .filter((value): value is number => typeof value === "number")

  const enjoymentTrend = getMoodTrend(thisWeekEnjoyment, previousWeekEnjoyment)

  const xTickFormatter = (tick: string) => {
    return getWeekdayAbbrev(tick)
  }

  const intensityColors = [
    colors.graphs.enjoyment.intensity100,
    colors.graphs.enjoyment.intensity200,
    colors.graphs.enjoyment.intensity300,
  ]

  const getIntensityColor = (datum: EnjoymentChartDatum) => {
    const intensity = datum.enjoyment ?? 1
    const index = Math.max(
      0,
      Math.min(
        intensityColors.length - 1,
        Math.round(((intensity - 1) / 4) * (intensityColors.length - 1))
      )
    )

    return intensityColors[index]
  }

  const alertComponent = (): React.ReactNode => {
    if (!enjoymentTrend || enjoymentTrend.direction === "no_change") {
      return null
    }

    const isEnjoymentIncreasing = enjoymentTrend.direction === "increasing"

    return (
      <ChartAlert
        association={isEnjoymentIncreasing ? "positive" : "negative"}
        variant={enjoymentTrend.direction}
        changeAmount={Number(enjoymentTrend.meanChange.toFixed(1))}
        message="enjoyment score this week"
      />
    )
  }
  const Alert = alertComponent()

  return (
    <ChartCard title="Enjoyment">
      <BarChart
        data={chartData}
        xKey={"dateKey"}
        yKey={"enjoyment"}
        maxY={5}
        yTicks={[1, 2, 3, 4, 5]}
        yAxisTickLabelComponent={<EnjoymentChartLabel />}
        xTickFormatter={xTickFormatter}
        getBarColor={(d) => getIntensityColor(d)}
        chartPadding={{ top: 16, right: 16, bottom: 25, left: 40 }}
        height={150}
        width={420}
      />

      <div className={styles.footer}>
        <div className={styles.spacer} />
        {Alert ? <div className={styles.alert}>{alertComponent()}</div> : null}
      </div>
    </ChartCard>
  )
}

const useStyles = makeStyles({
  footer: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginTop: 8,
    flexWrap: "nowrap",
  },
  spacer: {
    flex: "1 1 auto",
    minWidth: 0,
  },
  alert: {
    flex: "0 0 auto",
    marginLeft: "auto",
  },
})
